import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib';

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  className?: string;
}

export function Modal({ open, onOpenChange, title, description, children, className }: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-fade-in" />
        <Dialog.Content
          // Radix povezuje Content sa Description preko aria-describedby i upozorava
          // kada opisa nema. Bez opisa se veza eksplicitno iskljucuje.
          {...(description ? {} : { 'aria-describedby': undefined })}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2',
            'max-h-[90vh] flex flex-col',
            'rounded-xl border border-admin-border bg-admin-surface text-admin-text shadow-lg',
            'data-[state=open]:animate-slide-up',
            'focus:outline-none',
            className
          )}
        >
          <div className="shrink-0 px-6 pt-6 mb-4 flex items-start justify-between">
            <div>
              <Dialog.Title className="text-lg font-semibold text-admin-text">{title}</Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-admin-text-secondary">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              className="rounded-lg p-1.5 text-admin-text-secondary hover:bg-admin-surface-hover hover:text-admin-text transition-colors"
              aria-label="Zatvori"
            >
              <X className="h-4 w-4" />
            </Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0 px-6 pb-6">{children}</div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function ModalFooter({
  children,
  className,
  'data-testid': testId,
}: {
  children: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}) {
  return (
    // -bottom-6/-mb-6/pb-6: the modal body keeps a 24px bottom padding, and a
    // footer stuck at bottom-0 stops above it, leaving a strip where scrolled
    // content shows through under the buttons. Extending the footer over that
    // strip keeps the bar opaque down to the modal's edge; the negative margin
    // and the padding cancel out, so a modal that does not scroll looks the same.
    <div
      className={cn(
        'sticky -bottom-6 -mb-6 bg-admin-surface pt-4 pb-6 flex justify-end gap-3',
        className
      )}
      data-testid={testId}
    >
      {children}
    </div>
  );
}
